import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Book, ArrowLeft, Eye } from 'lucide-react';

const HELP_STORAGE_KEY = 'admin_help_items';

const CATEGORY_INFO = {
  getting_started: { name: 'Getting Started', icon: '🚀', color: 'from-blue-500 to-cyan-500' },
  uploading: { name: 'Uploading Content', icon: '📤', color: 'from-green-500 to-emerald-500' },
  posting: { name: 'Posting & Social', icon: '📱', color: 'from-purple-500 to-pink-500' },
  self_marketing: { name: 'Self-Marketing', icon: '🎯', color: 'from-orange-500 to-red-500' },
  billing: { name: 'Billing & Plans', icon: '💳', color: 'from-yellow-500 to-orange-500' },
  technical: { name: 'Technical Support', icon: '🔧', color: 'from-gray-500 to-gray-600' },
  general: { name: 'General', icon: '📖', color: 'from-slate-500 to-slate-600' }
};

export default function HelpCenter() {
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadArticles();
  }, [selectedCategory, searchQuery]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const stored = localStorage.getItem(HELP_STORAGE_KEY);
      let items = stored ? JSON.parse(stored) : [];

      // Fallback seed for public view if nothing exists yet
      if (!Array.isArray(items) || items.length === 0) {
        items = [
          {
            id: 'article-1',
            title: 'Getting Started',
            content: 'Welcome to Consistency. Learn how to onboard and request approvals.',
            status: 'approved',
            type: 'article',
            category: 'getting_started',
            created_at: new Date().toISOString(),
            view_count: 0
          },
          {
            id: 'faq-1',
            title: 'How do I submit content?',
            content: 'Use CREATE → Assisted Autopilot to submit links or uploads.',
            status: 'approved',
            type: 'faq',
            category: 'uploading',
            created_at: new Date().toISOString(),
            view_count: 0
          }
        ];
      }

      // Only approved, non-archived items are public
      let filtered = items.filter((item) => item.status === 'approved');

      if (selectedCategory) {
        filtered = filtered.filter((item) => item.category === selectedCategory);
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.title.toLowerCase().includes(query) ||
            (item.content || '').toLowerCase().includes(query)
        );
      }

      setArticles(filtered);
    } catch (err) {
      console.error('Error loading articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupedArticles = useMemo(() => {
    return articles.reduce((acc, article) => {
      if (!acc[article.category]) {
        acc[article.category] = [];
      }
      acc[article.category].push(article);
      return acc;
    }, {});
  }, [articles]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Book className="w-10 h-10 text-cyan-400" />
              <div>
                <h1 className="text-4xl font-bold text-white">Help Center</h1>
                <p className="text-gray-400 mt-1">Find answers and get support</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white transition-colors"
            >
              Back to App
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {!selectedCategory && !searchQuery ? (
          // Category Grid
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Browse by Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(CATEGORY_INFO).map(([key, info]) => {
                const count = groupedArticles[key]?.length || 0;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className="p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg hover:border-cyan-500/50 transition-all text-left"
                  >
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${info.color} flex items-center justify-center text-3xl mb-4`}>
                      {info.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{info.name}</h3>
                    <p className="text-gray-400 text-sm">{count} articles</p>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          // Articles List
          <div>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchQuery('');
                }}
                className="p-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {selectedCategory ? CATEGORY_INFO[selectedCategory]?.name : 'Search Results'}
                </h2>
                <p className="text-gray-400 text-sm">{articles.length} articles found</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
              </div>
            ) : articles.length > 0 ? (
              <div className="space-y-4">
                {articles.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => navigate(`/help/article/${article.id}`)}
                    className="w-full p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-lg hover:border-cyan-500/50 transition-all text-left"
                  >
                    <h3 className="text-xl font-semibold text-white mb-2">{article.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {article.view_count || 0} views
                      </span>
                      <span>{new Date(article.created_at).toLocaleDateString()}</span>
                      <span className="uppercase text-xs">{article.type}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg">No articles found</p>
              </div>
            )}
          </div>
        )}

        {/* Contact Support */}
        <div className="mt-12 p-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-white mb-2">Still need help?</h3>
          <p className="text-gray-400 mb-6">Can't find what you're looking for? Contact our support team.</p>
          <button
            onClick={() => navigate('/contact-support')}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}
